import { useState } from 'react';
import { X, Droplets, Wind, Thermometer, Leaf, Users, Bug, Brain, Zap, Shield, Sprout, Fish, Mountain } from 'lucide-react';
import { useEmergencyBoost } from '../../hooks/usePlanet';

interface EmergencyModalProps {
  planetId: string;
  stats: Record<string, number>;
  geography?: { landArea?: number; waterCoverage?: number };
  credits: number;
  stage?: number;
  onClose: () => void;
}

const BOOSTS = [
  { key: 'water', label: 'Water', icon: <Droplets className="w-5 h-5" />, amount: 5, rate: 4, color: 'text-blue-400', bg: 'bg-blue-950/40 border-blue-800/50', minStage: 0 },
  { key: 'atmosphere', label: 'Atmosphere', icon: <Wind className="w-5 h-5" />, amount: 5, rate: 4, color: 'text-sky-400', bg: 'bg-sky-950/40 border-sky-800/50', minStage: 0 },
  { key: 'stability', label: 'Stability', icon: <Shield className="w-5 h-5" />, amount: 5, rate: 4, color: 'text-emerald-400', bg: 'bg-emerald-950/40 border-emerald-800/50', minStage: 0 },
  { key: 'temperature', label: 'Temperature', icon: <Thermometer className="w-5 h-5" />, amount: 5, rate: 6, color: 'text-orange-400', bg: 'bg-orange-950/40 border-orange-800/50', minStage: 0 },
  { key: 'oxygen', label: 'Oxygen', icon: <Sprout className="w-5 h-5" />, amount: 5, rate: 6, color: 'text-cyan-400', bg: 'bg-cyan-950/40 border-cyan-800/50', minStage: 1 },
  { key: 'biomass', label: 'Biomass', icon: <Leaf className="w-5 h-5" />, amount: 5, rate: 6, color: 'text-green-400', bg: 'bg-green-950/40 border-green-800/50', minStage: 2 },
  { key: 'biodiversity', label: 'Biodiversity', icon: <Fish className="w-5 h-5" />, amount: 3, rate: 8, color: 'text-teal-400', bg: 'bg-teal-950/40 border-teal-800/50', minStage: 2 },
  { key: 'primitiveLife', label: 'Primitive Life', icon: <Bug className="w-5 h-5" />, amount: 3, rate: 8, color: 'text-lime-400', bg: 'bg-lime-950/40 border-lime-800/50', minStage: 2 },
  { key: 'animalLife', label: 'Animal Life', icon: <Zap className="w-5 h-5" />, amount: 2, rate: 10, color: 'text-yellow-400', bg: 'bg-yellow-950/40 border-yellow-800/50', minStage: 4 },
  { key: 'population', label: 'Population', icon: <Users className="w-5 h-5" />, amount: 2, rate: 10, color: 'text-amber-400', bg: 'bg-amber-950/40 border-amber-800/50', minStage: 5 },
  { key: 'technology', label: 'Technology', icon: <Brain className="w-5 h-5" />, amount: 2, rate: 12, color: 'text-violet-400', bg: 'bg-violet-950/40 border-violet-800/50', minStage: 5 },
  { key: 'land', label: 'Land', icon: <Mountain className="w-5 h-5" />, amount: 5, rate: 5, color: 'text-amber-400', bg: 'bg-amber-950/40 border-amber-800/50', minStage: 1 },
];

export default function EmergencyModal({ planetId, stats, geography, credits, stage = 0, onClose }: EmergencyModalProps) {
  const [confirming, setConfirming] = useState<string | null>(null);
  const boost = useEmergencyBoost(planetId);

  const handleBoost = (key: string, amount: number) => {
    if (confirming === key) {
      boost.mutate({ stat: key, amount });
      setConfirming(null);
    } else {
      setConfirming(key);
      setTimeout(() => setConfirming(null), 3000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="relative max-w-lg w-full max-h-[85vh] overflow-y-auto p-5 rounded-2xl bg-slate-900 border border-red-500/30 shadow-2xl shadow-red-900/10">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-slate-800 transition-colors"
        >
          <X className="w-5 h-5 text-slate-400" />
        </button>

        <div className="mb-4">
          <h2 className="text-xl font-bold text-red-100">Emergency Supplies</h2>
          <p className="text-sm text-slate-400 mt-1">
            Running low on a critical stat? Buy an emergency boost to get back on track.
            Costs are high — upgrades are always better value.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {BOOSTS.filter((b) => stage >= b.minStage).map((b) => {
            const cost = b.amount * b.rate;
            const current = b.key === 'land'
              ? Math.round(geography?.landArea || 0)
              : Math.round(stats[b.key] || 0);
            const canAfford = credits >= cost;
            const isConfirming = confirming === b.key;

            return (
              <button
                key={b.key}
                onClick={() => canAfford && handleBoost(b.key, b.amount)}
                disabled={!canAfford || boost.isPending}
                className={`
                  flex items-center gap-3 p-3 rounded-xl border text-left transition-all
                  ${b.bg}
                  ${canAfford ? 'hover:brightness-110 cursor-pointer' : 'opacity-50 cursor-not-allowed'}
                  ${isConfirming ? 'ring-2 ring-red-400' : ''}
                `}
              >
                <div className={`shrink-0 ${b.color}`}>{b.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-200">{b.label}</span>
                    <span className={`text-xs font-mono ${b.color}`}>{current}</span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-xs text-slate-500">+{b.amount}</span>
                    <span className={`text-xs font-mono ${canAfford ? 'text-yellow-400' : 'text-red-400'}`}>
                      {isConfirming ? 'Click again to confirm' : `${cost}cr`}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 pt-3 border-t border-slate-800 flex items-center justify-between">
          <span className="text-xs text-slate-500">Available credits</span>
          <span className="text-sm font-mono font-semibold text-yellow-400">{credits}cr</span>
        </div>
      </div>
    </div>
  );
}
